import { useApi } from '@/api';
import {
  Asset,
  HyperLiquidLink,
  Link,
  LinkButton,
  MultiKpiCard,
  Page,
  PageSkeleton,
  RecentActivity
} from '@/components';
import { useChecksumAddress } from '@/hooks';
import {
  Box,
  Card,
  Flex,
  Heading,
  KpiCard,
  LinkWrapper,
  PercentageValue,
  WalletAddress
} from '@blockanalitica/ui';
import { ReactNode, useCallback } from 'react';
import { useParams } from 'react-router';
import HistoricRatesTabs from './HistoricRatesTabs';
import HistoricTabs from './HistoricTabs';
import Params from './Params';
import { DEFAULT_NETWORK } from '@/constants';

type Market = {
  underlying_symbol: string;
  underlying_address: string;
  underlying_name: string;
  total_supply: string;
  total_supply_usd: string;
  total_supply_change: string | null;
  total_supply_change_percentage: string | null;
  total_borrow: string;
  total_borrow_usd: string;
  total_borrow_change: string | null;
  total_borrow_change_percentage: string | null;
  supply_apy: string;
  supply_apy_change: string | null;
  supply_apy_change_percentage: string | null;
  supply_apy_1d: string;
  supply_apy_7d: string;
  supply_apy_30d: string;
  borrow_apy: string;
  borrow_apy_change: string | null;
  borrow_apy_change_percentage: string | null;
  borrow_apy_1d: string;
  borrow_apy_7d: string;
  borrow_apy_30d: string;
  utilization_rate: string;
  utilization_rate_change: string | null;
  utilization_rate_change_percentage: string | null;
  available_liquidity_change_percentage: string | null;
  underlying_price: string;
  underlying_price_change: string | null;
  underlying_price_change_percentage: string | null;
  num_of_borrowers: string;
  num_of_borrowers_change: string | null;
  num_of_borrowers_change_percentage: string | null;
  num_of_suppliers: string;
  num_of_suppliers_change: string | null;
  num_of_suppliers_change_percentage: string | null;
  atoken_address: string;
  variable_debt_token_address: string;
  interest_rate_strategy_address: string;
  total_collateral: string | null;
  status: {
    borrow: string;
    collateral: string;
    supply: string;
    frozen: string;
  };
};

export type ActivityEvent = {
  datetime: string;
  date: string;
  tx_hash: string;
  block_number: string;
  order_index: string;
  wallet_address: string;
  event: string;
  underlying_address: string;
  symbol: string;
  debt_symbol?: string;
  amount: string;
  amount_usd: string;
};

export default function MarketPage() {
  const { marketAddress } = useParams() as {
    marketAddress: string;
  };

  const checksumAddress = useChecksumAddress(marketAddress);

  const network = DEFAULT_NETWORK;
  const [data, error] = useApi<Market>({
    url: `/markets/${marketAddress}/`
  });

  const contentFn = useCallback(
    (item: ActivityEvent) => (
      <Flex gap="s" alignItems="center">
        {item.event} {item.symbol}
        <Flex color="muted" gap="2xs">
          Wallet:
          <Link to={`wallets/${item.wallet_address}`} relative="path">
            <WalletAddress
              address={item.wallet_address}
              options={{ showBlockie: false }}
            />
          </Link>
        </Flex>
      </Flex>
    ),
    []
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return <PageSkeleton />;
  }

  function KpiCardLinkComponent({
    data,
    field,
    to,
    title
  }: {
    data: Market;
    field: string;
    to: string;
    title: ReactNode;
  }) {
    return (
      <>
        {data[field as keyof Market] !== '0' &&
        data[field as keyof Market] !== null ? (
          <LinkWrapper
            to={to}
            // @ts-ignore
            as={Link}
            variant="kpiCardLink">
            <KpiCard
              title={title}
              data={data}
              field={field}
              options={{
                trend: true,
                notation: 'compact',
                linkArrow: true,
                currency: 'USD',
                valueOptions: {
                  currencyValueOptions: {
                    tooltip: true
                  }
                }
              }}
            />
          </LinkWrapper>
        ) : (
          <KpiCard
            title={title}
            data={data}
            field={field}
            options={{
              notation: 'compact',
              linkArrow: false,
              currency: 'USD',
              valueOptions: {
                currencyValueOptions: {
                  nullable: '-',
                  tooltip: true
                }
              }
            }}
          />
        )}
      </>
    );
  }

  return (
    <Page
      title={
        <Asset
          symbol={data.underlying_symbol}
          address={marketAddress}
          network={network}
          iconSize="4"
          fontSize="5"
        />
      }
      actions={
        <Flex gap="2xs">
          <LinkButton
            variant="primary"
            size="small"
            as="a"
            // @ts-ignore
            href={`https://app.hyperlend.finance/markets/${checksumAddress}`}
            rel="noreferrer"
            target="_blank">
            Supply/Borrow
          </LinkButton>
        </Flex>
      }>
      <Flex flexDirection={['column', 'row']} gap={['m', 's']}>
        <KpiCard
          title="Total Supplied"
          data={data}
          field="total_supply"
          options={{
            notation: 'compact',
            trend: true,
            valueOptions: {
              currencyValueOptions: {
                tooltip: true
              }
            }
          }}
        />

        <KpiCard
          title="Total Available"
          data={data}
          field="available_liquidity"
          options={{
            notation: 'compact',
            trend: true,
            valueOptions: {
              currencyValueOptions: {
                tooltip: true
              }
            }
          }}
        />
        <KpiCard
          title="Total Borrowed"
          data={data}
          field="total_borrow"
          options={{
            notation: 'compact',
            trend: true,
            valueOptions: {
              currencyValueOptions: {
                tooltip: true
              }
            }
          }}
        />
      </Flex>
      <HistoricTabs
        underlyingSymbol={data.underlying_symbol}
        underlyingAddress={data.underlying_address}
        network={network}
      />

      <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
        <MultiKpiCard
          title="Supply APY"
          data={data}
          field="supply_apy"
          kpis={[
            {
              title: '1D AVG',
              content: <PercentageValue value={data.supply_apy_1d} />
            },
            {
              title: '7D AVG',
              content: <PercentageValue value={data.supply_apy_7d} />
            },
            {
              title: '30D AVG',
              content: <PercentageValue value={data.supply_apy_30d} />
            }
          ]}
          options={{
            percentage: true,
            variants: {
              valuePercentageValue: 'dynamic'
            }
          }}
        />
        <MultiKpiCard
          title="Borrow APY"
          data={data}
          field="borrow_apy"
          kpis={[
            {
              title: '1D AVG',
              content: <PercentageValue value={data.borrow_apy_1d} />
            },
            {
              title: '7D AVG',
              content: <PercentageValue value={data.borrow_apy_7d} />
            },
            {
              title: '30D AVG',
              content: <PercentageValue value={data.borrow_apy_30d} />
            }
          ]}
          options={{
            percentage: true,
            variants: {
              valuePercentageValue: 'dynamic'
            }
          }}
        />
      </Flex>

      <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
        <Card flexBasis="33%" justifyContent="space-between" gap="s">
          <Flex flexDirection="column" gap="2xs">
            <Heading size="4" paddingBottom="xs">
              {data.underlying_name} Contracts
            </Heading>
            <Flex justifyContent="space-between">
              <Box color="muted">{data.underlying_symbol}</Box>
              <Flex alignItems="center" gap="2xs">
                <WalletAddress
                  address={data.underlying_address}
                  options={{
                    inline: true,
                    showBlockie: false,
                    showCopyBtn: true,
                    network
                  }}
                />
                <HyperLiquidLink path={`address/${data.underlying_address}`} />
              </Flex>
            </Flex>
            <Flex justifyContent="space-between">
              <Box color="muted">hHyperEvm{data.underlying_symbol}</Box>
              <Flex alignItems="center" gap="2xs">
                <WalletAddress
                  address={data.atoken_address}
                  options={{
                    inline: true,
                    showBlockie: false,
                    showCopyBtn: true,
                    network
                  }}
                />
                <HyperLiquidLink path={`address/${data.atoken_address}`} />
              </Flex>
            </Flex>
            <Flex justifyContent="space-between">
              <Box color="muted">Variable Debt {data.underlying_symbol}</Box>
              <Flex alignItems="center" gap="2xs">
                <WalletAddress
                  address={data.variable_debt_token_address}
                  options={{
                    inline: true,
                    showBlockie: false,
                    showCopyBtn: true,
                    network
                  }}
                />
                <HyperLiquidLink
                  path={`address/${data.variable_debt_token_address}`}
                />
              </Flex>
            </Flex>
            <Flex justifyContent="space-between">
              <Box color="muted">Interest Rate Strategy</Box>
              <Flex alignItems="center" gap="2xs">
                <WalletAddress
                  address={data.interest_rate_strategy_address}
                  options={{
                    inline: true,
                    showBlockie: false,
                    showCopyBtn: true,
                    network
                  }}
                />
                <HyperLiquidLink
                  path={`address/${data.interest_rate_strategy_address}`}
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex flexDirection="column" gap={['s', 'xs']}>
            <KpiCard
              title="Utilization rate"
              data={data}
              field="utilization_rate"
              variant="nested"
              options={{
                percentage: true,
                variants: {
                  valuePercentageValue: 'dynamic'
                }
              }}
            />
            <KpiCard
              title="Oracle price"
              data={data}
              field="underlying_price"
              variant="nested"
              options={{
                currency: 'USD'
              }}
            />
          </Flex>
        </Card>

        <HistoricRatesTabs marketAddress={marketAddress} flexBasis="67%" />
      </Flex>

      <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
        <Flex gap={['m', 's']} flexDirection="column" flex="1">
          <LinkWrapper
            to="wallets?type=suppliers"
            // @ts-ignore
            as={Link}
            variant="kpiCardLink">
            <KpiCard
              title="N° of suppliers"
              data={data}
              field="num_of_suppliers"
              options={{
                trend: true,
                variants: { valuePercentageValue: 'dynamic' },
                linkArrow: true
              }}
            />
          </LinkWrapper>
          <LinkWrapper
            to="wallets?type=borrowers"
            // @ts-ignore
            as={Link}
            variant="kpiCardLink">
            <KpiCard
              title="N° of borrowers"
              data={data}
              field="num_of_borrowers"
              options={{
                trend: true,
                variants: { valuePercentageValue: 'dynamic' },
                linkArrow: true
              }}
            />
          </LinkWrapper>
          <KpiCardLinkComponent
            data={data}
            field="total_debt"
            to="collateral"
            title={`Total debt backed by ${data.underlying_symbol} `}
          />
          <KpiCardLinkComponent
            data={data}
            field="total_collateral"
            to="debt"
            title={`Total collateral backing ${data.underlying_symbol} debt`}
          />
        </Flex>

        <Params
          marketAddress={marketAddress}
          supply={data.total_supply}
          borrow={data.total_borrow}
        />
      </Flex>

      <RecentActivity<ActivityEvent>
        activityAPIUrl={`/markets/${marketAddress}/activity/`}
        activityContentFn={contentFn}
        heatmapAPIUrl={`/markets/${marketAddress}/heatmap/`}
        linkTo="activity"
      />
    </Page>
  );
}
